import { useCallback } from 'react';
import { create } from 'zustand';

export type OfertasFiltersValueState = Record<string, string[]>;

type State = {
  selectedAll: boolean;
  selectedIds: string[];
  excludedIds: string[];
  selectedLabel: {
    normalLabel: string;
    strongLabel: string;
  };
  ofertasFilters: OfertasFiltersValueState;
  ofertasFiltersCampaignId: string | null;
};

type StoreActions = {
  setSelectedIds: (selectedIds: string[]) => void;
  setExcludedIds: (excludedIds: string[]) => void;
  setSelectedAll: (selectedAll: boolean) => void;
  setOfertasFilters: (campaignId: string, ofertasFilters: OfertasFiltersValueState) => void;
  updateOfertasFilters: (campaignId: string, key: string, value: string[]) => void;
  resetOfertasFilters: (campaignId: string) => void;
};

const initialState: State = {
  selectedAll: false,
  selectedIds: [],
  excludedIds: [],
  selectedLabel: {
    normalLabel: 'Foi encontrada ',
    strongLabel: '0 oferta ',
  },
  ofertasFilters: {},
  ofertasFiltersCampaignId: null,
};

const campanhaStore = create<State & StoreActions>(set => ({
  ...initialState,
  setSelectedIds: (selectedIds: string[]) => {
    set(state => ({
      ...state,
      selectedIds,
      selectedLabel: {
        normalLabel: selectedIds.length > 1 ? `Foram encontradas ` : `Foi encontrada `,
        strongLabel: selectedIds.length > 1 ? `${selectedIds.length} ofertas ` : `${selectedIds.length} oferta `,
      },
    }));
  },
  setExcludedIds: (excludedIds: string[]) => {
    set(state => ({
      ...state,
      excludedIds,
    }));
  },
  setSelectedAll: (selectedAll: boolean) => {
    set(state => ({
      ...state,
      selectedAll,
    }));
  },
  setOfertasFilters: (campaignId: string, ofertasFilters: OfertasFiltersValueState) => {
    set(state => ({
      ...state,
      ofertasFilters,
      ofertasFiltersCampaignId: campaignId,
    }));
  },
  updateOfertasFilters: (campaignId: string, key: string, value: string[]) => {
    set(state => ({
      ...state,
      ofertasFilters: {
        ...(state.ofertasFiltersCampaignId === campaignId ? state.ofertasFilters : {}),
        [key]: value,
      },
      ofertasFiltersCampaignId: campaignId,
    }));
  },
  resetOfertasFilters: (campaignId: string) => {
    set(state => {
      if (state.ofertasFiltersCampaignId !== campaignId) return state;

      return {
        ...state,
        ofertasFilters: {},
        ofertasFiltersCampaignId: null,
      };
    });
  },
}));

export const useCampanhaStore = (campaignId: string) => {
  const selectedIds = campanhaStore(state => state.selectedIds);
  const selectedLabel = campanhaStore(state => state.selectedLabel);
  const storedOfertasFilters = campanhaStore(state => state.ofertasFilters);
  const ofertasFiltersCampaignId = campanhaStore(state => state.ofertasFiltersCampaignId);
  const selectedAll = campanhaStore(state => state.selectedAll);
  const setStoreOfertasFilters = campanhaStore(state => state.setOfertasFilters);
  const updateStoreOfertasFilters = campanhaStore(state => state.updateOfertasFilters);
  const resetStoreOfertasFilters = campanhaStore(state => state.resetOfertasFilters);
  const setSelectedAll = campanhaStore(state => state.setSelectedAll);
  const setSelectedIds = campanhaStore(state => state.setSelectedIds);
  const setExcludedIds = campanhaStore(state => state.setExcludedIds);

  const setOfertasFilters = useCallback(
    (ofertasFilters: OfertasFiltersValueState) => {
      setStoreOfertasFilters(campaignId, ofertasFilters);
    },
    [campaignId, setStoreOfertasFilters],
  );

  const updateOfertasFilters = useCallback(
    (key: string, value: string[]) => {
      updateStoreOfertasFilters(campaignId, key, value);
    },
    [campaignId, updateStoreOfertasFilters],
  );

  const resetOfertasFilters = useCallback(() => {
    resetStoreOfertasFilters(campaignId);
  }, [campaignId, resetStoreOfertasFilters]);

  return {
    selectedIds,
    selectedLabel,
    ofertasFilters: ofertasFiltersCampaignId === campaignId ? storedOfertasFilters : {},
    selectedAll,
    setOfertasFilters,
    updateOfertasFilters,
    resetOfertasFilters,
    setSelectedAll,
    setSelectedIds,
    setExcludedIds,
  };
};
